import { formatDate } from '../../utils/format';
import { discountError } from '../../utils/amountField';
import { rabatWGroszach } from '../../utils/rabatRezerwacji';
import { isCancelledReservation, isCancelledStatusName } from '../../utils/reservationStatus';
import { jestMetodaNaMiejscu } from '../../utils/metodaWplaty';
import { SIZE_FIELD_KEYS } from '../../utils/inventoryFieldDefs';

// Kody statusu rezerwacji (bez języka — mapowanie na etykietę i ton robi widok przez t()).
// `overdue` (X.1) nie ma odpowiednika w bazie: w `reservation_status` spóźniona jest „W trakcie".
export const RESERVATION_STATUS_CODES = ['upcoming', 'ongoing', 'overdue', 'finished', 'cancelled'];

// Kolory statusu jako referencje do tokenów motywu (nigdy hex) — działają w obu motywach.
export const STATUS_STYLES = {
  upcoming: { fg: 'var(--info)', bg: 'var(--info-bg)' },
  ongoing: { fg: 'var(--success)', bg: 'var(--success-bg)' },
  overdue: { fg: 'var(--warning)', bg: 'var(--warning-bg)' },
  finished: { fg: 'var(--text-muted)', bg: 'var(--hover-bg)' },
  cancelled: { fg: 'var(--danger)', bg: 'var(--danger-bg)' },
};

// X.1: stan wydania i zwrotu ze sztuk rezerwacji. Wiersz listy niesie `equipment` (id sztuk)
// oraz `picked_up_equipment`/`returned_equipment` (id sztuk wydanych i zwróconych, migracja 045).
// LUSTRO reguły z Login/backend/lib/stanWydania.js (panel nie importuje backendu): „u klienta" =
// wydana i niezwrócona, „wrócił cały sprzęt" = są sztuki i wróciły wszystkie.
export function handoverState(r) {
  const ids = (list) => new Set((Array.isArray(list) ? list : []).filter((x) => x != null).map(Number));
  const all = ids(r?.equipment);
  const picked = ids(r?.picked_up_equipment);
  const returned = ids(r?.returned_equipment);
  const total = all.size || Number(r?.equipment_count) || 0;
  let out = 0;
  picked.forEach((id) => { if (!returned.has(id)) out += 1; });
  return {
    total,
    pickedUp: picked.size,
    returned: returned.size,
    out,
    allReturned: total > 0 && returned.size >= total,
    recorded: picked.size > 0 || returned.size > 0,
  };
}

// Ile pełnych dni minęło od dnia końca rezerwacji (0 = termin dziś albo w przyszłości).
export function daysPastEnd(r, now = new Date()) {
  const end = parseYMD(r?.end_date);
  if (!end || Number.isNaN(end.getTime())) return 0;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(0, Math.round((today - endDay) / MS_PER_DAY));
}

// Status z wydania/zwrotu (X.1), a gdy niczego nie odnotowano — z dat.
// Spóźniona = coś jest u klienta, a dzień końca minął (decyzja Jacka 2026-10-23: tylko po
// odnotowanym wydaniu; rezerwacja bez wydania kończy się z dat).
// Daty po DNIACH, jak sweeper (lib/stanWydania.js#statusDocelowySql): ostatni dzień rezerwacji
// jest jeszcze „w trakcie" (zwrot bywa wieczorem). Dawniej porównanie z północą końca kończyło
// rezerwację o 0:00 jej ostatniego dnia, więc „Do zwrotu dziś" pokazywałoby „Zakończona".
export function computeStatus(r, now = new Date()) {
  const h = handoverState(r);
  if (h.allReturned) return 'finished';
  if (h.out > 0) return daysPastEnd(r, now) > 0 ? 'overdue' : 'ongoing';
  const today = dayKey(now);
  const start = dayKey(r.start_date);
  const end = dayKey(r.end_date);
  if (start && today < start) return 'upcoming';
  if (end && today > end) return 'finished';
  return 'ongoing';
}

// Status karty i okna szczegółów: anulowana z bazy wygrywa, potem wydanie/zwrot (liczone tu, bo
// status w bazie przelicza się dopiero przy odczycie listy, a „spóźnionej" baza nie zna), potem
// status z bazy, a bez niego daty.
export function reservationStatusCode(r, now = new Date()) {
  if (!r) return '';
  const db = normalizeStatus(r.reservation_status || r.reservation_status_name);
  if (db === 'cancelled') return 'cancelled';
  if (handoverState(r).recorded) return computeStatus(r, now);
  return db || computeStatus(r, now);
}

// Normalizuje status z backendu (PL/EN, różne warianty) do kodu. Wejście NIE zmienia się —
// dalej rozpoznaje te same warianty tekstowe co dotychczas, zmienia się tylko zwracana wartość.
export function normalizeStatus(s) {
  if (!s) return '';
  // Anulowaną rozpoznaje JEDNA reguła panelu (utils/reservationStatus.js, lustro serwera). Dawne
  // `includes('anul')` łapało też np. „Nieanulowana", której Statystyki i widget nie pomijają.
  if (isCancelledStatusName(s)) return 'cancelled';
  const n = String(s).toLowerCase();
  if (n.includes('nadchod')) return 'upcoming';
  if (n.includes('trak') || n.includes('trwaj') || n.includes('ongo')) return 'ongoing';
  if (n.includes('zako') || n.includes('finish')) return 'finished';
  return '';
}

export function isSameDayOrInRange(date, start, end) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return s <= d && d <= e;
}

// Uniwersalna funkcja parsująca pole rabatu z obiektu rezerwacji
export const parseDiscountPercent = (r) => {
  if (!r) return 0;
  const cand = r.discount_percentage ?? r.discount_percent ?? r.discount ?? r.discount_amount ?? null;
  if (cand == null) return 0;
  const num = Number(cand);
  if (Number.isNaN(num)) return 0;

  // 0 < num <= 1  => traktujemy jako ułamek (0.05 -> 5%)
  // 1 < num <=100 => traktujemy jako procent (5 -> 5%)
  if (num > 0 && num <= 1) return Number((num * 100).toFixed(6));
  if (num > 1 && num <= 100) return Number(num);

  // inaczej traktujemy jako kwotę - próbujemy policzyć % względem subtotal/total_price
  const base = Number(r.subtotal ?? r.total_price ?? 0);
  if (base > 0) return Number(((num / base) * 100).toFixed(6));
  return 0;
}

// DODANE: pomocnicze funkcje bezpieczne dla stref czasowych
export const formatYMD = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const parseYMD = (s) => {
  if (!s) return null;
  if (s instanceof Date) return s;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])); // lokalna północ
  // fallback
  const d = new Date(s);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// DODANE: bezpieczne liczenie dni — zakres inkluzywny (Od i Do włącznie)
export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const startOfDaySafe = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : parseYMD(d);
  return x ? new Date(x.getFullYear(), x.getMonth(), x.getDate()) : null;
};

export const startOfDayLocal = (d) => startOfDaySafe(d);

// Poczatek dzisiejszego dnia liczony PRZY KAZDYM wywolaniu. Dawniej byla to stala liczona raz przy
// imporcie modulu, a panel przy ladzie bywa otwarty przez noc (sesja trwa do 14 dni): nastepnego
// dnia kalendarzyk edycji odblokowywal wczoraj, a zapis go przepuszczal (przeglad 28.09.2026).
export const getStartOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const daysInclusive = (start, end) => {
  const s = startOfDaySafe(start);
  const e = startOfDaySafe(end);
  if (!s || !e) return 0;
  const diff = Math.round((e - s) / MS_PER_DAY);
  return diff >= 0 ? diff + 1 : 0;
};

// U.10: stabilne przypisanie „torów" (swim-lanes) rezerwacjom w widoku miesiąca.
// Bez tego każdy kafelek dnia niezależnie upychał nakładające się rezerwacje flexboxem
// od góry (`slice(0,2)` po kolejności tablicy, nie po dacie), więc wielodniowy pasek
// przeskakiwał o tor gdy sąsiednia krótsza rezerwacja się kończyła i renderował się
// „na" niej. Greedy interval-graph packing po dacie startu daje każdej rezerwacji stały
// numer toru, zajmowany przez CAŁY jej zakres dni — dokładnie jak w kalendarzach pro.
// Zwraca { laneById: Map<id, lane>, laneCount }.
export const assignReservationLanes = (reservations) => {
  const items = (Array.isArray(reservations) ? reservations : [])
    .map((r) => {
      // TA SAMA reguła dnia co kafelki siatki i plan dnia (`dayKey`, czyli `parseYMD`): data bez
      // godziny to dzień LOKALNY, żeby tor rezerwacji zgadzał się z dniami, na których stoi pasek.
      const s = r?.start_date != null ? startOfDaySafe(r.start_date) : null;
      const e = r?.end_date != null ? startOfDaySafe(r.end_date) : null;
      const valid = (d) => d && !Number.isNaN(d.getTime());
      return valid(s) && valid(e) ? { id: r.id, start: s.getTime(), end: e.getTime() } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.start - b.start) || (a.end - b.end) || (Number(a.id) - Number(b.id)));

  const laneEnds = []; // laneEnds[i] = koniec (ms) ostatniej rezerwacji ułożonej w torze i
  const laneById = new Map();

  for (const it of items) {
    // najniższy tor, którego ostatnia rezerwacja kończy się PRZED startem bieżącej
    // (koniec < start ⇒ brak wspólnego dnia ⇒ brak nakładania); inaczej nowy tor
    let lane = laneEnds.findIndex((end) => end < it.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.end);
    } else {
      laneEnds[lane] = it.end;
    }
    laneById.set(it.id, lane);
  }

  return { laneById, laneCount: laneEnds.length };
};

// Klucz dnia RRRR-MM-DD w czasie LOKALNYM; '' dla wartości, która nie jest datą. JEDNA reguła
// „do którego dnia należy ta data" dla siatki miesiąca, torów i planu dnia: data bez godziny
// (`2026-12-20`) to ten dzień u użytkownika, a nie północ UTC, która na zachód od Greenwich
// wypada dzień wcześniej. Klucze porównują się jak daty, bez arytmetyki na milisekundach.
export const dayKey = (value) => {
  const d = parseYMD(value);
  return d && !Number.isNaN(d.getTime()) ? formatYMD(d) : '';
};

// U.29: plan dnia BEZ godzin (rezerwacje ich nie mają, godziny to U.31). Odpowiada na trzy
// pytania przy ladzie: co dziś wydać, co dziś wróci, co jest u klientów; do tego lista sprzętu
// do przygotowania, czyli sztuki z wydań tego dnia zliczone po typie i rozmiarze.
//
// Wejście: wiersze /api/all-reservations (`equipment` to tablica SAMYCH id), `Map` sprzętu po id
// zbudowana z GET /api/equipment (albo nic, gdy magazyn się nie wczytał) i dzień (Date albo
// RRRR-MM-DD). Rezerwacja jednodniowa jest JEDNOCZEŚNIE wydaniem i zwrotem, więc trafia do obu
// sekcji i nie do „trwających". Anulowane odpadają tutaj (utils/reservationStatus.js, jedyna
// reguła panelu), niezależnie od tego, co przefiltrował widok.
//
// Wpis sekcji to obiekt `{ reservation, items, oneDay }`, a nie sama rezerwacja: znaczniki
// „wydano" / „zwrócono" (X.1) dojdą jako kolejne pola wpisu i sztuki, bez zmiany kształtu.
//
// Sztuka, której nie ma w mapie (dodana do magazynu po otwarciu kalendarza), nie wywraca planu:
// zostaje na liście rezerwacji jako `known: false`, liczy się w `prepare.unknown`, a jej id
// trafia do `missing`, po którym ekran dociąga magazyn.
//
// N.1: wymiana tego samego dnia (`wymiana`, flaga firmy z Zasad wynajmu). Sztuka z wydań, która tego
// dnia ma jeszcze wrócić od INNEJ rezerwacji, dostaje `returnsToday: true`, a `prepare.returningToday`
// liczy takie sztuki: tu widać, co trzeba odebrać, zanim da się to wydać. Sztuka już zwrócona (także
// wcześniej, przed terminem) albo już wydana nie ma na co czekać, a rezerwacja jednodniowa nie
// „wraca” do samej siebie. Oba pola pojawiają się tylko wtedy, gdy jest co pokazać, i tylko przy
// włączonej wymianie: plan bez niej ma kształt jak dotąd, nawet gdy dwie rezerwacje jednej sztuki
// spotkają się w jednym dniu (opłacona oferta z ostrzeżeniem o konflikcie).
const PLAN_COLLATOR = new Intl.Collator('pl', { numeric: true, sensitivity: 'base' });

const planText = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text || null;
};

// Rozmiar sztuki: pole Rozmiar, a gdy jest puste, pierwsze wypełnione pole rozmiarowe z metadata.
// Kolejność i lista kluczy pochodzą z definicji pól. „-” to atrapa, którą kod sprzed E.5 zapisywał
// przy pustym rozmiarze (migracja 040 tych wierszy nie czyściła), więc liczy się jak puste.
const planSize = (eq) => {
  for (const value of [eq.size, ...SIZE_FIELD_KEYS.map((key) => eq.metadata?.[key])]) {
    const text = planText(value);
    if (text && text !== '-') return text;
  }
  return null;
};

const planClientName = (r) => `${r?.lastname || ''} ${r?.firstname || ''}`.trim();

export function buildDayPlan(reservations, equipmentById, day, { wymiana = false } = {}) {
  // `count` = ile RÓŻNYCH rezerwacji dotyczy dnia (jednodniowa stoi w dwóch sekcjach, liczy się raz).
  const plan = {
    count: 0, pickups: [], returns: [], ongoing: [], missing: [], prepare: { total: 0, unknown: 0, groups: [] },
  };
  const key = dayKey(day);
  if (!key) return plan;

  const missing = new Set();
  // X.1: każda sztuka niesie też znaczniki wydania i zwrotu (wiersz listy podaje ich id).
  const itemsOf = (r) => {
    const seen = new Set();
    const items = [];
    const picked = new Set((r.picked_up_equipment || []).map(Number));
    const returned = new Set((r.returned_equipment || []).map(Number));
    (Array.isArray(r.equipment) ? r.equipment : []).forEach((id) => {
      if (id == null || seen.has(id)) return;
      seen.add(id);
      const eq = equipmentById?.get?.(id);
      if (!eq) missing.add(id);
      items.push(eq
        ? {
          id,
          known: true,
          name: planText(eq.name),
          type: planText(eq.category),
          size: planSize(eq),
          inventoryNumber: planText(eq.inventory_number),
          pickedUp: picked.has(Number(id)),
          returned: returned.has(Number(id)),
        }
        : {
          id, known: false, name: null, type: null, size: null, inventoryNumber: null,
          pickedUp: picked.has(Number(id)), returned: returned.has(Number(id)),
        });
    });
    return items;
  };

  (Array.isArray(reservations) ? reservations : []).forEach((r) => {
    if (!r || isCancelledReservation(r)) return;
    const start = dayKey(r.start_date);
    const end = dayKey(r.end_date);
    if (!start || !end || key < start || key > end) return;
    const entry = { reservation: r, items: itemsOf(r), oneDay: start === end };
    plan.count += 1;
    if (start === key) plan.pickups.push(entry);
    if (end === key) plan.returns.push(entry);
    if (start !== key && end !== key) plan.ongoing.push(entry);
  });
  plan.missing = [...missing];

  const byClient = (a, b) =>
    PLAN_COLLATOR.compare(planClientName(a.reservation), planClientName(b.reservation))
    || (Number(a.reservation.id) - Number(b.reservation.id));
  plan.pickups.sort(byClient);
  plan.returns.sort(byClient);
  plan.ongoing.sort(byClient);

  const wracajaZ = new Map(); // id sztuki -> rezerwacje, od których ma dziś wrócić
  if (wymiana) {
    plan.returns.forEach(({ reservation, items }) => items.forEach((item) => {
      if (item.returned) return;
      if (!wracajaZ.has(item.id)) wracajaZ.set(item.id, new Set());
      wracajaZ.get(item.id).add(reservation.id);
    }));
  }

  const groups = new Map(); // typ -> { type, count, sizes: Map<rozmiar, liczba> }
  plan.pickups.forEach(({ reservation, items }) => items.forEach((item) => {
    plan.prepare.total += 1;
    if (!item.pickedUp && [...(wracajaZ.get(item.id) || [])].some((id) => id !== reservation.id)) {
      item.returnsToday = true;
      plan.prepare.returningToday = (plan.prepare.returningToday || 0) + 1;
    }
    if (!item.known) {
      plan.prepare.unknown += 1;
      return;
    }
    const typeKey = item.type || '';
    if (!groups.has(typeKey)) groups.set(typeKey, { type: item.type, count: 0, sizes: new Map() });
    const group = groups.get(typeKey);
    group.count += 1;
    const sizeKey = item.size || '';
    group.sizes.set(sizeKey, (group.sizes.get(sizeKey) || 0) + 1);
  }));

  plan.prepare.groups = [...groups.values()]
    .map((group) => ({
      type: group.type,
      count: group.count,
      sizes: [...group.sizes.entries()]
        .map(([size, count]) => ({ size: size || null, count }))
        // rozmiary rosnąco („42" przed „150", „S" po liczbach), sztuki bez rozmiaru na końcu
        .sort((a, b) => (a.size === null) - (b.size === null) || PLAN_COLLATOR.compare(a.size || '', b.size || '')),
    }))
    // najliczniejszy typ pierwszy, sprzęt bez typu na końcu
    .sort((a, b) => (a.type === null) - (b.type === null)
      || (b.count - a.count)
      || PLAN_COLLATOR.compare(a.type || '', b.type || ''));

  return plan;
}

// Typ, którego żadna sztuka nie ma rozmiaru (kaski, kijki), pokazuje samą liczbę: dopisek
// „bez rozmiaru ×5" pod „Kaski ×5" niczego by nie dodawał.
export const planGroupHasSizes = (group) =>
  Array.isArray(group?.sizes) && group.sizes.some((s) => s.size !== null);

// locale/noDateLabel przychodzą z wywołania (ten moduł jest czysty, bez useTranslation) —
// widok podaje currentLanguage i t('calendar.noDate').
export const formatConflictDate = (value, locale, noDateLabel = '—') => {
  if (!value) return noDateLabel;
  const parsed = parseYMD(value);
  if (parsed && !Number.isNaN(parsed.getTime())) {
    return formatDate(parsed, { locale });
  }
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime())
    ? String(value)
    : formatDate(fallback, { locale });
};

export const getEquipmentDailyRate = (equipment, isLongTermRental) => {
  if (!equipment) return 0;
  const base = Number(equipment.price_per_day ?? equipment.price ?? 0) || 0;
  const longTerm =
    Number(
      equipment.price_over_7 ??
      equipment.priceOver7 ??
      equipment.long_term_price ??
      0
    ) || 0;
  return isLongTermRental && longTerm > 0 ? longTerm : base;
};

// Polska odmiana słowa "rezerwacja" w zależności od liczby
export const reservationsWord = (n) => {
  if (n === 1) return 'rezerwacja';
  const d = n % 10;
  const h = n % 100;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'rezerwacje';
  return 'rezerwacji';
};

// Kwota po rabacie w pełnych groszach, tą samą regułą co faktura i link do zapłaty
// (utils/rabatRezerwacji.js, N.6): rabat procentowy zaokrąglony do grosza przed odjęciem.
export const calculateFinalPrice = (r) => {
  const subtotal = Number(r.total_price || 0);
  const discountUnit = String(r.discount_unit || '%').toUpperCase();
  const discountValue = Number(r.discount || r.discount_value || r.discount_amount || 0);

  if (discountValue === 0 || subtotal === 0) return subtotal;

  const sumaGr = Math.round(subtotal * 100);
  const rabat = rabatWGroszach(sumaGr, discountValue, discountUnit === '%' ? '%' : 'PLN');
  return Math.max(0, sumaGr - rabat) / 100;
};

export const formatDiscountDisplay = (v) => {
  const num = Number(v);
  if (!isFinite(num) || num === 0) return '0';
  const fixed = num.toFixed(2);
  // usuń niepotrzebne końcowe zera: 5.00 -> 5, 5.50 -> 5.5, 5.25 -> 5.25
  return fixed.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

// P3.16: opłata za anulowanie / selektywny zwrot.
// Wejście: lista opłaconych płatności online (advance/balance, z polami amount_grosze/amount_refunded_grosze),
// kwota opłaty do ZATRZYMANIA (feeRaw) i jednostka ('PLN' | '%'). Zwrot = suma pozostała − opłata.
// Zwrot alokujemy NAJPIERW na 'balance' (oddajemy w całości), resztę zatrzymujemy na 'advance'
// (klasyczna opłata za anulowanie = przepadek zaliczki). Wszystko w groszach (liczby całkowite).
// Zwraca { totalPaidGrosze, feeGrosze, refundGrosze, allocation:[{paymentId, amount_grosze}], feeError } —
// allocation pomija płatności z zerowym zwrotem (te są w całości zatrzymane).
//
// `feeError` (12.10.2026, test odbiorczy płatności): kod błędu opłaty z `utils/amountField.js#discountError`
// ('amountOver' gdy opłata w zł przekracza wpłatę, 'percentOver' ponad 100%, 'invalid'), albo `null`.
// Wcześniej nadwyżkę przycinano po cichu do całej wpłaty: pomyłka „500” zamiast „50” dawała „Do zwrotu
// 0,00 zł”, przycisk zmieniał się na zwykłe „Anuluj rezerwację” i klient zostawał bez zwrotu. Kwoty
// niżej nadal są przycięte (podgląd nie może pokazać ujemnego zwrotu), ale okno przy `feeError` blokuje
// anulowanie, dopóki opłata nie zostanie poprawiona. Opłata RÓWNA wpłacie jest poprawna (zatrzymanie całości).
export function computeCancellationRefund(refundable, feeRaw, feeUnit = 'PLN') {
  const items = (refundable || [])
    .map((p) => ({
      paymentId: p.id,
      type: p.type,
      remaining: Math.max(0, Math.round(Number(p.amount_grosze) - Number(p.amount_refunded_grosze || 0))),
    }))
    .filter((it) => it.remaining > 0);
  const totalPaidGrosze = items.reduce((s, it) => s + it.remaining, 0);

  // Akceptuj polski przecinek dziesiętny ('150,50') tak samo jak kropkę.
  const raw = Math.max(0, Number(String(feeRaw).replace(',', '.')) || 0);
  let feeGrosze = feeUnit === '%'
    ? Math.round((totalPaidGrosze * Math.min(raw, 100)) / 100)
    : Math.round(raw * 100);
  feeGrosze = Math.min(feeGrosze, totalPaidGrosze); // opłata nie może przekroczyć wpłaconego
  const refundGrosze = totalPaidGrosze - feeGrosze;

  // balance najpierw (0), advance na końcu (1), reszta typów (2) — determinizm rozdziału.
  const order = { balance: 0, advance: 1 };
  const sorted = [...items].sort((a, b) => (order[a.type] ?? 2) - (order[b.type] ?? 2));
  let left = refundGrosze;
  const allocation = [];
  for (const it of sorted) {
    if (left <= 0) break;
    const amt = Math.min(it.remaining, left);
    if (amt > 0) {
      allocation.push({ paymentId: it.paymentId, amount_grosze: amt });
      left -= amt;
    }
  }
  const feeError = totalPaidGrosze > 0
    ? discountError(feeRaw === null || feeRaw === undefined ? '' : String(feeRaw), feeUnit === '%' ? '%' : 'PLN', totalPaidGrosze / 100)
    : null;
  return { totalPaidGrosze, feeGrosze, refundGrosze, allocation, feeError };
}

// Pieniądze przy rezerwacji, którą obsługa chce anulować (12.10.2026, test odbiorczy płatności).
// Wejście: wiersze z GET /api/payments?reservation_id=… (trasa oddaje je także pracownikowi).
// Do tej pory okno anulowania znało tylko płatności online do zwrotu i tylko u administratora:
// wpłata gotówką albo przelewem, zablokowana kaucja i cała reszta u pracownika nie były wspomniane,
// więc pieniądze klienta zostawały po cichu przy anulowanej rezerwacji. Kwoty netto po zwrotach,
// w groszach, tak jak liczy je serwer (routes/reservations.js, DELETE).
//   refundable          płatności online do zwrotu przez Stripe (wejście computeCancellationRefund),
//   offlineGrosze        przyjęte na miejscu (gotówka, przelew), oddaje się je ręcznie,
//   offlineMethods       metody tych wpłat ('cash', 'transfer', 'card' = terminal, F.10), do podpisu,
//   depositsAuthorized   kaucje zablokowane na karcie (do zwolnienia), [{ id, amount_grosze }],
//   depositCapturedGrosze kaucja pobrana (z karty albo gotówką) minus zwroty,
//   invoicePayments      wpłaty wpisane ręcznie na fakturach rezerwacji, [{ number, grosze }]
//                        (`invoice_payments` z tej samej trasy, przegląd pakietu 3b): rejestr ich nie
//                        zna, a serwer i tak nie pozwala pracownikowi anulować takiej rezerwacji,
//   invoiceGrosze        ich suma,
//   anyMoney             czy przy rezerwacji są jakiekolwiek przyjęte pieniądze.
export function cancellationMoney(payments, invoicePayments = []) {
  const lista = Array.isArray(payments) ? payments : [];
  const naFakturach = (Array.isArray(invoicePayments) ? invoicePayments : [])
    .map((f) => ({ number: f && f.number ? String(f.number) : '', grosze: Math.max(0, Math.round(Number(f && f.grosze) || 0)) }))
    .filter((f) => f.grosze > 0);
  const invoiceGrosze = naFakturach.reduce((s, f) => s + f.grosze, 0);
  const netto = (p) => Math.max(0, Math.round(Number(p.amount_grosze) - Number(p.amount_refunded_grosze || 0)));
  const oplacona = (p) => p.type !== 'deposit' && ['paid', 'partially_refunded'].includes(p.status) && netto(p) > 0;
  const refundable = lista.filter((p) => oplacona(p) && p.provider === 'stripe' && p.gateway_order_id);
  const naMiejscu = lista.filter((p) => oplacona(p) && p.provider === 'offline');
  // Wpłata online bez identyfikatora płatności Stripe (np. stary operator) nie da się zwrócić z panelu,
  // ale to nadal pieniądze klienta, więc liczy się do „przyjętych” razem z wpłatami na miejscu.
  const bezZwrotuOnline = lista.filter((p) => oplacona(p) && p.provider !== 'offline' && !(p.provider === 'stripe' && p.gateway_order_id));
  const offlineGrosze = [...naMiejscu, ...bezZwrotuOnline].reduce((s, p) => s + netto(p), 0);
  const offlineMethods = [...new Set(naMiejscu.map((p) => p.method).filter(jestMetodaNaMiejscu))];
  const depositsAuthorized = lista
    .filter((p) => p.type === 'deposit' && p.status === 'authorized')
    .map((p) => ({ id: p.id, amount_grosze: Math.max(0, Math.round(Number(p.amount_grosze) || 0)), releasable: !!p.gateway_order_id }));
  const depositCapturedGrosze = lista
    .filter((p) => p.type === 'deposit' && ['captured', 'partially_refunded'].includes(p.status))
    .reduce((s, p) => {
      const pobrana = Number(p.captured_grosze) > 0 ? Number(p.captured_grosze) : Number(p.amount_grosze) || 0;
      return s + Math.max(0, Math.round(pobrana - Number(p.amount_refunded_grosze || 0)));
    }, 0);
  const onlineGrosze = refundable.reduce((s, p) => s + netto(p), 0);
  const depositAuthorizedGrosze = depositsAuthorized.reduce((s, d) => s + d.amount_grosze, 0);
  return {
    refundable,
    onlineGrosze,
    offlineGrosze,
    offlineMethods,
    depositsAuthorized,
    depositAuthorizedGrosze,
    depositCapturedGrosze,
    invoicePayments: naFakturach,
    invoiceGrosze,
    anyMoney: onlineGrosze + offlineGrosze + depositAuthorizedGrosze + depositCapturedGrosze + invoiceGrosze > 0,
  };
}

// P3.16 (code-review #4): oczyść wejście opłaty — zostaw cyfry i TYLKO pierwszy separator (kropka
// LUB przecinek), kolejne usuń. Bez tego '1,2,3' → NaN → opłata 0 → pełny zwrot. computeCancellationRefund
// normalizuje ',' → '.', więc pojedynczy przecinek jest OK.
export function sanitizeFeeInput(v) {
  let seen = false;
  return String(v ?? '').replace(/[^0-9.,]/g, '').replace(/[.,]/g, (m) => (seen ? '' : ((seen = true), m)));
}

/**
 * Czy panel dnia trzeba przewinąć do widoku po wyborze dnia w kalendarzu. Na wąskim ekranie
 * (iPad w pionie, telefon) panel leży POD kalendarzem i po stuknięciu w pasek rezerwacji jej
 * karta pojawiała się poza ekranem, więc wyglądało to, jakby nic się nie stało (symulator
 * iOS 26.5, 27.09.2026). Obok kalendarza (szeroki ekran) panel jest widoczny i nic nie ruszamy.
 * Próg: początek panelu w dolnej ćwiartce okna albo niżej, czyli karty i tak nie widać.
 */
export function panelDniaPozaWidokiem(gornaKrawedz, wysokoscOkna) {
  if (!Number.isFinite(gornaKrawedz) || !Number.isFinite(wysokoscOkna) || wysokoscOkna <= 0) return false;
  return gornaKrawedz > wysokoscOkna * 0.75;
}
